import { __ } from '@wordpress/i18n';
import {
	useBlockProps,
	InspectorControls,
	MediaUpload,
	MediaUploadCheck,
	PanelColorSettings,
} from '@wordpress/block-editor';

import {
	PanelBody,
	SelectControl,
	RangeControl,
	Button,
	TextControl,
	ToggleControl,
} from '@wordpress/components';

import './editor.scss';

export default function Edit({ attributes, setAttributes }) {

	const {
		logos,
		template,
		animationDuration,
		showAltText,
		logoSize,
		slideDirection,
		displayMode,
		gridColumns,
		masonryColumns,
		wrapperBgColor,
		itemBgColor,
		itemBorderColor,
		itemBorderWidth,
		itemBorderRadius,
		altTextColor,
		showTooltip,
		tooltipPosition,
		multiRowCount,
	} = attributes;

	const blockProps = useBlockProps();
	const isPro = window.wlcProData?.isPro || false;

	// ── Logo CRUD ───────────────────────────────────────────────────
	const addLogo = (media) => {
		setAttributes({
			logos: [...logos, { id: media.id, url: media.url, alt: media.alt || '', link: '', tooltip: '' }]
		});
	};

	const updateLogo = (index, field, value) => {
		const updated = [...logos];
		updated[index][field] = value;
		setAttributes({ logos: updated });
	};

	const removeLogo = (index) => {
		setAttributes({ logos: logos.filter((_, i) => i !== index) });
	};

	// ── Links ───────────────────────────────────────────────────────
	const upgradeLink = 'https://checkout.freemius.com/plugin/24598/plan/40853/licenses/1/';
	const rateLink    = 'https://wordpress.org/support/plugin/wbd-logo-carousel/reviews/#new-post';
	const pageLink    = 'https://www.wpbranddigital.org/contact/';

	const isProTemplate = (v) => ['luxury','modern','minimal','premium','neon','glassmorphism'].includes(v);
	const isProDisplay  = (v) => ['grid','masonry','multirow'].includes(v);

	// ── Inline styles ───────────────────────────────────────────────
	const itemStyle = (() => {
		const s = {};
		if (itemBgColor)     s.background  = itemBgColor;
		if (itemBorderColor) s.borderColor = itemBorderColor;
		if (itemBorderWidth) { s.borderWidth = `${itemBorderWidth}px`; s.borderStyle = 'solid'; }
		if (itemBorderRadius !== undefined && itemBorderRadius !== null)
			s.borderRadius = `${itemBorderRadius}px`;
		return s;
	})();

	const wrapperStyle = (extra = {}) => ({
		...(wrapperBgColor ? { background: wrapperBgColor } : {}),
		...extra,
	});

	const altStyle = altTextColor ? { color: altTextColor } : {};

	// ── Tooltip position diagram labels ────────────────────────────
	const positionLabels = { top: '↑ Top', bottom: '↓ Bottom', left: '← Left', right: '→ Right' };

	// ── Split logos for multirow ────────────────────────────────────
	const splitIntoRows = (arr, rowCount) => {
		if (!arr.length) return Array.from({ length: rowCount }, () => []);
		const rows = Array.from({ length: rowCount }, () => []);
		arr.forEach((item, i) => rows[i % rowCount].push(item));
		return rows;
	};

	// ── Logo item renderer (editor preview) ────────────────────────
	// Note: in editor we render tooltip as static visible for preview;
	// on frontend it is controlled by JS + fixed positioning.
	const renderItem = (logo, index, cls) => {
		const hasTooltip = isPro && showTooltip && logo.tooltip;
		return (
			<div
				key={index}
				className={`${cls}${hasTooltip ? ` has-tooltip tooltip-${tooltipPosition}` : ''}`}
				style={itemStyle}
				data-tooltip={hasTooltip ? logo.tooltip : undefined}
				data-tooltip-position={hasTooltip ? tooltipPosition : undefined}
			>
				{logo.link ? (
					<a href={logo.link}>
						<img src={logo.url} alt={logo.alt} style={{ maxWidth: `${logoSize}px` }} />
						{showAltText && logo.alt && (
							<span className="logo-carousel__alt-text" style={altStyle}>{logo.alt}</span>
						)}
					</a>
				) : (
					<>
						<img src={logo.url} alt={logo.alt} style={{ maxWidth: `${logoSize}px` }} />
						{showAltText && logo.alt && (
							<span className="logo-carousel__alt-text" style={altStyle}>{logo.alt}</span>
						)}
					</>
				)}
				{/* Tooltip bubble — JS will move this to fixed position on frontend */}
				{hasTooltip && (
					<div className={`logo-tooltip logo-tooltip--${tooltipPosition}`}>{logo.tooltip}</div>
				)}
			</div>
		);
	};

	const EmptyNotice = () => (
		<div className="wlcs-add-logo-section">
			<p>⚠️ No logos added. Please add logos from the sidebar.</p>
		</div>
	);

	// ── Layout previews ─────────────────────────────────────────────
	const renderCarouselPreview = () => (
		<div
			className={`logo-carousel logo-carousel--${template} logo-carousel--${slideDirection}`}
			style={wrapperStyle({ '--animation-duration': `${animationDuration}s` })}
		>
			{logos.length === 0 && <EmptyNotice />}
			<div className="logo-carousel__track">
				{logos.concat(logos).map((logo, i) => renderItem(logo, i, 'logo-carousel__item'))}
			</div>
		</div>
	);

	const renderMultirowPreview = () => {
		const rows = splitIntoRows(logos, multiRowCount);
		return (
			<div
				className={`logo-multirow${!isPro ? ' logo-layout--pro-locked' : ''}`}
				style={wrapperStyle({ '--animation-duration': `${animationDuration}s` })}
			>
				{logos.length === 0 && <EmptyNotice />}
				{rows.map((rowLogos, rowIndex) => {
					const dir = rowIndex % 2 === 0 ? 'left' : 'right';
					return (
						<div key={rowIndex} className={`logo-multirow__row logo-multirow__row--${dir}`}>
							<div className="logo-carousel__track">
								{rowLogos.concat(rowLogos).map((logo, i) => renderItem(logo, i, 'logo-carousel__item'))}
							</div>
						</div>
					);
				})}
				{!isPro && <div className="logo-layout__pro-overlay">🔒 Unlock Pro Layout</div>}
			</div>
		);
	};

	const renderGridPreview = () => (
		<div
			className={`logo-grid logo-grid--${template}${!isPro ? ' logo-layout--pro-locked' : ''}`}
			style={wrapperStyle({ '--grid-columns': gridColumns })}
		>
			{logos.length === 0 && <EmptyNotice />}
			{logos.map((logo, i) => renderItem(logo, i, 'logo-grid__item'))}
			{!isPro && <div className="logo-layout__pro-overlay">🔒 Unlock Pro Layout</div>}
		</div>
	);

	const renderMasonryPreview = () => (
		<div
			className={`logo-masonry logo-masonry--${template}${!isPro ? ' logo-layout--pro-locked' : ''}`}
			style={wrapperStyle({ '--masonry-columns': masonryColumns })}
		>
			{logos.length === 0 && <EmptyNotice />}
			{logos.map((logo, i) => renderItem(logo, i, 'logo-masonry__item'))}
			{!isPro && <div className="logo-layout__pro-overlay">🔒 Unlock Pro Layout</div>}
		</div>
	);

	// ── Sidebar ─────────────────────────────────────────────────────
	return (
		<>
			<InspectorControls>

				{/* PRO BUTTON */}
				<PanelBody title="Unlock Pro Features & Help">
					<p style={{ marginBottom: '10px' }}>Upgrade to Pro to unlock advanced layouts, themes &amp; more.</p>
					<div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
						<Button isPrimary onClick={() => window.open(upgradeLink, '_blank')}>Get Pro Version</Button>
						<Button isPrimary onClick={() => window.open(rateLink, '_blank')}>Rate Plugin</Button>
						<Button isPrimary onClick={() => window.open(pageLink, '_blank')}>Contact Support</Button>
					</div>
				</PanelBody>

				{/* DISPLAY MODE */}
				<PanelBody title="Display Mode" initialOpen={true}>
					<SelectControl
						label={__('Layout Mode', 'wbd-logo-carousel')}
						value={displayMode}
						options={[
							{ label: __('Carousel (Slider)', 'wbd-logo-carousel'),                                                                  value: 'carousel' },
							{ label: isPro ? __('Multi-Row Carousel', 'wbd-logo-carousel') : __('Multi-Row Carousel (Pro)', 'wbd-logo-carousel'), value: 'multirow' },
							{ label: isPro ? __('Grid', 'wbd-logo-carousel')               : __('Grid (Pro)', 'wbd-logo-carousel'),               value: 'grid'     },
							{ label: isPro ? __('Masonry', 'wbd-logo-carousel')            : __('Masonry (Pro)', 'wbd-logo-carousel'),            value: 'masonry'  },
						]}
						onChange={(value) => setAttributes({ displayMode: value })}
					/>
					{!isPro && isProDisplay(displayMode) && (
						<p style={{ color: '#d63638', fontSize: '12px', marginTop: '6px' }}>
							⭐ Requires <a href={upgradeLink} target="_blank" rel="noreferrer">Pro version</a>.
						</p>
					)}
				</PanelBody>

				{/* THEME */}
				<PanelBody title="Theme Style">
					<SelectControl
						label={__('Theme Style', 'wbd-logo-carousel')}
						value={template}
						options={[
							{ label: __('Classic', 'wbd-logo-carousel'),                                                                        value: 'classic'       },
							{ label: __('Elegant', 'wbd-logo-carousel'),                                                                        value: 'elegant'       },
							{ label: isPro ? __('Luxury', 'wbd-logo-carousel')        : __('Luxury (Pro)', 'wbd-logo-carousel'),        value: 'luxury'        },
							{ label: isPro ? __('Modern', 'wbd-logo-carousel')        : __('Modern (Pro)', 'wbd-logo-carousel'),        value: 'modern'        },
							{ label: isPro ? __('Minimal', 'wbd-logo-carousel')       : __('Minimal (Pro)', 'wbd-logo-carousel'),       value: 'minimal'       },
							{ label: isPro ? __('Premium', 'wbd-logo-carousel')       : __('Premium (Pro)', 'wbd-logo-carousel'),       value: 'premium'       },
							{ label: isPro ? __('Neon', 'wbd-logo-carousel')          : __('Neon (Pro)', 'wbd-logo-carousel'),          value: 'neon'          },
							{ label: isPro ? __('Glassmorphism', 'wbd-logo-carousel') : __('Glassmorphism (Pro)', 'wbd-logo-carousel'), value: 'glassmorphism' },
						]}
						onChange={(value) => setAttributes({ template: value })}
					/>
					{!isPro && isProTemplate(template) && (
						<p style={{ color: '#d63638', fontSize: '12px', marginTop: '6px' }}>
							⭐ Requires <a href={upgradeLink} target="_blank" rel="noreferrer">Pro version</a>.
						</p>
					)}
				</PanelBody>

				{/* LOGO MANAGEMENT */}
				<PanelBody title="Manage Logos" initialOpen={true}>
					{logos.map((logo, index) => (
						<div key={index} className="logo-sidebar-card">
							<img className="logo-sidebar-image" src={logo.url} alt={logo.alt || ''} />
							<TextControl
								label="Link URL"
								value={logo.link}
								onChange={(value) => updateLogo(index, 'link', value)}
								placeholder="https://"
							/>
							<TextControl
								label="Alt Text"
								value={logo.alt}
								onChange={(value) => updateLogo(index, 'alt', value)}
							/>
							{isPro && showTooltip && (
								<TextControl
									label="Tooltip Text"
									value={logo.tooltip || ''}
									onChange={(value) => updateLogo(index, 'tooltip', value)}
									placeholder="e.g. Google — Partner since 2020"
									help="Shown on hover over this logo"
								/>
							)}
							{!isPro && showTooltip && (
								<p style={{ fontSize: '11px', color: '#d63638', marginTop: '4px' }}>
									⭐ Tooltip requires <a href={upgradeLink} target="_blank" rel="noreferrer">Pro</a>.
								</p>
							)}
							<Button isDestructive onClick={() => removeLogo(index)}>Remove Logo</Button>
						</div>
					))}
					<MediaUploadCheck>
						<MediaUpload
							onSelect={addLogo}
							allowedTypes={['image']}
							render={({ open }) => (
								<Button
									onClick={open} variant="primary"
									style={{ marginTop: '10px', width: '100%', display: 'flex', justifyContent: 'center' }}
								>
									+ Add Logo
								</Button>
							)}
						/>
					</MediaUploadCheck>
				</PanelBody>

				{/* CAROUSEL SETTINGS */}
				{displayMode === 'carousel' && (
					<PanelBody title="Carousel Settings">
						<SelectControl
							label="Slide Direction"
							value={slideDirection}
							options={[
								{ label: 'Left',  value: 'left'  },
								{ label: 'Right', value: 'right' },
							]}
							onChange={(value) => setAttributes({ slideDirection: value })}
						/>
						<RangeControl
							label="Animation Duration (seconds)"
							value={animationDuration}
							onChange={(value) => setAttributes({ animationDuration: value })}
							min={10} max={60} step={5}
						/>
					</PanelBody>
				)}

				{/* MULTI-ROW SETTINGS */}
				{displayMode === 'multirow' && (
					<PanelBody title="Multi-Row Carousel Settings">
						<p style={{ fontSize: '12px', color: '#757575', marginBottom: '12px' }}>
							Logos split across rows. Odd rows scroll left ←, even rows scroll right →.
						</p>
						<RangeControl
							label="Number of Rows"
							value={multiRowCount}
							onChange={(value) => setAttributes({ multiRowCount: value })}
							min={2} max={4} step={1}
						/>
						<RangeControl
							label="Animation Duration (seconds)"
							value={animationDuration}
							onChange={(value) => setAttributes({ animationDuration: value })}
							min={10} max={60} step={5}
						/>
						<div style={{ marginTop: '14px' }}>
							<p style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', color: '#757575', marginBottom: '8px' }}>
								Row Directions
							</p>
							{Array.from({ length: multiRowCount }, (_, i) => (
								<div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', fontSize: '12px', color: '#555' }}>
									<span style={{ background: i % 2 === 0 ? '#0ea5e9' : '#8b5cf6', color: '#fff', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: '600' }}>
										Row {i + 1}
									</span>
									<span>{i % 2 === 0 ? '← Scroll Left' : '→ Scroll Right'}</span>
								</div>
							))}
						</div>
					</PanelBody>
				)}

				{/* GRID SETTINGS */}
				{displayMode === 'grid' && (
					<PanelBody title="Grid Settings">
						<RangeControl
							label="Columns" value={gridColumns}
							onChange={(value) => setAttributes({ gridColumns: value })}
							min={2} max={6} step={1}
						/>
					</PanelBody>
				)}

				{/* MASONRY SETTINGS */}
				{displayMode === 'masonry' && (
					<PanelBody title="Masonry Settings">
						<RangeControl
							label="Columns" value={masonryColumns}
							onChange={(value) => setAttributes({ masonryColumns: value })}
							min={2} max={5} step={1}
						/>
					</PanelBody>
				)}

				{/* LOGO SETTINGS */}
				<PanelBody title="Logo Settings">
					<RangeControl
						label="Logo Size (px)" value={logoSize}
						onChange={(value) => setAttributes({ logoSize: value })}
						min={50} max={300} step={10}
					/>
					<ToggleControl
						label="Show Alt Text"
						checked={showAltText}
						onChange={(value) => setAttributes({ showAltText: value })}
					/>

					{/* ── TOOLTIP SECTION ── */}
					<div style={{ marginTop: '12px', padding: '12px', background: '#f9f9f9', borderRadius: '6px', border: '1px solid #e5e5e5' }}>
						<ToggleControl
							label={
								<span>
									{__('Show Hover Tooltip', 'wbd-logo-carousel')}
									{!isPro && (
										<span style={{ marginLeft: '6px', fontSize: '10px', background: '#f59e0b', color: '#fff', borderRadius: '3px', padding: '1px 5px', fontWeight: '700' }}>
											PRO
										</span>
									)}
								</span>
							}
							checked={showTooltip}
							onChange={(value) => setAttributes({ showTooltip: value })}
							help={isPro ? 'Add tooltip text per logo in Manage Logos above.' : 'Upgrade to Pro to enable hover tooltips.'}
						/>

						{/* Tooltip Position — only show when tooltip is ON */}
						{showTooltip && (
							<>
								<SelectControl
									label={__('Tooltip Position', 'wbd-logo-carousel')}
									value={tooltipPosition}
									options={[
										{ label: '↑ Top',    value: 'top'    },
										{ label: '↓ Bottom', value: 'bottom' },
										{ label: '← Left',   value: 'left'   },
										{ label: '→ Right',  value: 'right'  },
									]}
									onChange={(value) => setAttributes({ tooltipPosition: value })}
									style={{ marginTop: '8px' }}
								/>

								{/* Visual position diagram */}
								<div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gridTemplateRows: '1fr 1fr 1fr', gap: '4px', width: '120px', margin: '10px auto 0' }}>
									{/* Top */}
									<div style={{ gridColumn: '2', gridRow: '1' }}>
										<div
											onClick={() => setAttributes({ tooltipPosition: 'top' })}
											style={{
												background: tooltipPosition === 'top' ? '#2563eb' : '#e5e7eb',
												color: tooltipPosition === 'top' ? '#fff' : '#374151',
												borderRadius: '4px', padding: '4px', fontSize: '10px', textAlign: 'center', cursor: 'pointer', fontWeight: '600',
											}}
										>↑</div>
									</div>
									{/* Left */}
									<div style={{ gridColumn: '1', gridRow: '2', display: 'flex', alignItems: 'center' }}>
										<div
											onClick={() => setAttributes({ tooltipPosition: 'left' })}
											style={{
												background: tooltipPosition === 'left' ? '#2563eb' : '#e5e7eb',
												color: tooltipPosition === 'left' ? '#fff' : '#374151',
												borderRadius: '4px', padding: '4px', fontSize: '10px', textAlign: 'center', cursor: 'pointer', fontWeight: '600', width: '100%',
											}}
										>←</div>
									</div>
									{/* Center logo icon */}
									<div style={{ gridColumn: '2', gridRow: '2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
										<div style={{ width: '28px', height: '28px', background: '#d1d5db', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>🖼</div>
									</div>
									{/* Right */}
									<div style={{ gridColumn: '3', gridRow: '2', display: 'flex', alignItems: 'center' }}>
										<div
											onClick={() => setAttributes({ tooltipPosition: 'right' })}
											style={{
												background: tooltipPosition === 'right' ? '#2563eb' : '#e5e7eb',
												color: tooltipPosition === 'right' ? '#fff' : '#374151',
												borderRadius: '4px', padding: '4px', fontSize: '10px', textAlign: 'center', cursor: 'pointer', fontWeight: '600', width: '100%',
											}}
										>→</div>
									</div>
									{/* Bottom */}
									<div style={{ gridColumn: '2', gridRow: '3' }}>
										<div
											onClick={() => setAttributes({ tooltipPosition: 'bottom' })}
											style={{
												background: tooltipPosition === 'bottom' ? '#2563eb' : '#e5e7eb',
												color: tooltipPosition === 'bottom' ? '#fff' : '#374151',
												borderRadius: '4px', padding: '4px', fontSize: '10px', textAlign: 'center', cursor: 'pointer', fontWeight: '600',
											}}
										>↓</div>
									</div>
								</div>
								<p style={{ fontSize: '11px', color: '#6b7280', textAlign: 'center', marginTop: '6px' }}>
									Click to set position
								</p>
							</>
						)}
					</div>
				</PanelBody>

				{/* COLOR SETTINGS */}
				<PanelColorSettings
					title={__('Color Settings', 'wbd-logo-carousel')}
					initialOpen={false}
					colorSettings={[
						{ label: __('Wrapper Background', 'wbd-logo-carousel'), value: wrapperBgColor, onChange: (c) => setAttributes({ wrapperBgColor: c || '' }) },
						{ label: __('Card Background', 'wbd-logo-carousel'),    value: itemBgColor,    onChange: (c) => setAttributes({ itemBgColor: c || '' }) },
						{ label: __('Card Border Color', 'wbd-logo-carousel'),  value: itemBorderColor, onChange: (c) => setAttributes({ itemBorderColor: c || '' }) },
						{ label: __('Alt Text Color', 'wbd-logo-carousel'),     value: altTextColor,   onChange: (c) => setAttributes({ altTextColor: c || '' }) },
					]}
				/>

				{/* BORDER & RADIUS */}
				<PanelBody title={__('Border & Radius', 'wbd-logo-carousel')} initialOpen={false}>
					<RangeControl
						label={__('Border Width (px)', 'wbd-logo-carousel')}
						value={itemBorderWidth}
						onChange={(value) => setAttributes({ itemBorderWidth: value })}
						min={0} max={10} step={1} allowReset resetFallbackValue={0}
					/>
					<RangeControl
						label={__('Border Radius (px)', 'wbd-logo-carousel')}
						value={itemBorderRadius}
						onChange={(value) => setAttributes({ itemBorderRadius: value })}
						min={0} max={60} step={1} allowReset resetFallbackValue={8}
					/>
					<div style={{ marginTop: '16px' }}>
						<p style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', color: '#757575', marginBottom: '8px' }}>Live Preview</p>
						<div style={{
							height: '60px',
							background:   itemBgColor || '#ffffff',
							border:       `${itemBorderWidth ?? 1}px solid ${itemBorderColor || '#e0e0e0'}`,
							borderRadius: `${itemBorderRadius ?? 8}px`,
							display: 'flex', alignItems: 'center', justifyContent: 'center',
							fontSize: '12px', color: '#999', transition: 'all 0.2s ease',
						}}>Card preview</div>
					</div>
				</PanelBody>

			</InspectorControls>

			{/* LIVE PREVIEW */}
			<div {...blockProps}>
				{displayMode === 'carousel' && renderCarouselPreview()}
				{displayMode === 'multirow' && renderMultirowPreview()}
				{displayMode === 'grid'     && renderGridPreview()}
				{displayMode === 'masonry'  && renderMasonryPreview()}
			</div>
		</>
	);
}